import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { SkillTreeData, SkillNode, LearnSkillResponse, TalentPointTransaction, PassiveAbilityDefinition, SkillEffectData } from '../types/skillTree';
import './SkillTree.css';

interface SkillTreeProps {
  characterId: number;
  onNavigateToSkills?: () => void;
}

interface SkillNodePosition {
  skillId: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

const SkillTree: React.FC<SkillTreeProps> = ({ characterId, onNavigateToSkills }) => {
  const { token } = useAuth();
  const { checkAndStartTutorial } = useTutorial();
  const [skillTreeData, setSkillTreeData] = useState<SkillTreeData | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<SkillNode | null>(null);
  const [talentHistory, setTalentHistory] = useState<TalentPointTransaction[]>([]);
  const [passiveDefinitions, setPassiveDefinitions] = useState<PassiveAbilityDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [learningSkill, setLearningSkill] = useState<number | null>(null);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [skillPositions, setSkillPositions] = useState<Map<number, SkillNodePosition>>(new Map());
  const [hoveredSkill, setHoveredSkill] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [equippedSkills, setEquippedSkills] = useState<any[]>([]);
  const [draggedSkill, setDraggedSkill] = useState<SkillNode | null>(null);
  const skillRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    loadSkillTree();
    loadTalentHistory();
    loadPassiveDefinitions();
    loadEquippedSkills();
  }, [characterId]);

  // Auto-trigger skill tree tutorial for first-time players
  useEffect(() => {
    if (skillTreeData && !loading) {
      checkAndStartTutorial('skill-tree-basics');
    }
  }, [skillTreeData, loading, checkAndStartTutorial]);

  // Calculate skill node positions after render
  useEffect(() => {
    if (!skillTreeData) return;

    const calculatePositions = () => {
      const positions = new Map<number, SkillNodePosition>();
      const containerRect = document.querySelector('.skill-tree-tiers')?.getBoundingClientRect();

      if (!containerRect) return;

      skillRefs.current.forEach((element, skillId) => {
        const rect = element.getBoundingClientRect();
        positions.set(skillId, {
          skillId,
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
          width: rect.width,
          height: rect.height
        });
      });

      setSkillPositions(positions);
    };

    // Calculate positions after render
    calculatePositions();

    // Recalculate on window resize
    window.addEventListener('resize', calculatePositions);
    return () => window.removeEventListener('resize', calculatePositions);
  }, [skillTreeData]);

  const loadSkillTree = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`/skilltree/character/${characterId}`);

      if (!response.ok) {
        throw new Error('Failed to load skill tree');
      }

      const data = await response.json();
      setSkillTreeData(data);
      setError(null);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTalentHistory = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/skilltree/character/${characterId}/talent-history`);

      if (response.ok) {
        const data = await response.json();
        setTalentHistory(data);
      }
    } catch (err) {
      console.error('Failed to load talent history:', err);
    }
  };

  const loadPassiveDefinitions = async () => {
    try {
      const response = await makeAuthenticatedRequest('/skilltree/passive-abilities');

      if (response.ok) {
        const data = await response.json();
        setPassiveDefinitions(data);
      }
    } catch (err) {
      console.error('Failed to load passive definitions:', err);
    }
  };

  const loadEquippedSkills = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/skillhotbar/character/${characterId}`);

      if (response.ok) {
        const data = await response.json();
        setEquippedSkills(data);
      }
    } catch (err) {
      console.error('Failed to load equipped skills:', err);
    }
  };

  const equipSkillToHotbar = async (skillNodeId: number, hotbarSlot: number) => {
    try {
      const response = await makeAuthenticatedRequest('/skillhotbar/equip', {
        method: 'POST',
        body: JSON.stringify({
          characterId: characterId,
          skillNodeId: skillNodeId,
          hotbarSlot: hotbarSlot
        })
      });

      if (response.ok) {
        await loadEquippedSkills();
        setSuccessMessage('Skill equipped successfully!');
        setTimeout(() => setSuccessMessage(null), 2000);
      } else {
        const errorData = await response.json();
        console.error('Equip skill error:', errorData);
        setError(errorData.error || 'Failed to equip skill');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('Failed to equip skill:', err);
      setError('Failed to equip skill');
      setTimeout(() => setError(null), 3000);
    }
  };

  const unequipSkillFromHotbar = async (hotbarSlot: number) => {
    try {
      const response = await makeAuthenticatedRequest('/skillhotbar/unequip', {
        method: 'POST',
        body: JSON.stringify({
          characterId: characterId,
          hotbarSlot: hotbarSlot
        })
      });

      if (response.ok) {
        await loadEquippedSkills();
      }
    } catch (err) {
      console.error('Failed to unequip skill:', err);
    }
  };

  const learnSkill = async (skillNodeId: number) => {
    if (!skillTreeData || learningSkill) return;

    try {
      setLearningSkill(skillNodeId);
      const response = await makeAuthenticatedRequest(`/skilltree/character/${characterId}/learn/${skillNodeId}`, {
        method: 'POST'
      });

      if (!response.ok) {
        let errorMessage = 'Failed to learn skill';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If JSON parsing fails, try to get text
          const errorText = await response.text();
          errorMessage = errorText || `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const result: LearnSkillResponse = await response.json();

      if (result.success) {
        // Refresh skill tree data
        await loadSkillTree();
        await loadTalentHistory();
        setSelectedSkill(null);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    } finally {
      setLearningSkill(null);
    }
  };

  const resetSkills = async () => {
    if (!skillTreeData || resetting) return;

    try {
      setResetting(true);
      setShowResetConfirm(false);
      const response = await makeAuthenticatedRequest(`/skilltree/character/${characterId}/reset`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset skills');
      }

      const result = await response.json();

      if (result.success) {
        // Refresh all skill data including hotbar
        await loadSkillTree();
        await loadTalentHistory();
        await loadEquippedSkills();
        setSelectedSkill(null);
        setSuccessMessage(`Successfully reset ${result.skillsReset} skills and refunded ${result.talentPointsRefunded} talent points`);
        setTimeout(() => setSuccessMessage(null), 3000);

        // Navigate back to skills tab
        if (onNavigateToSkills) {
          onNavigateToSkills();
        }
      }
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    } finally {
      setResetting(false);
    }
  };

  const getSkillIcon = (skill: SkillNode): string => {
    // Map skill categories to icons/emojis
    const iconMap: { [key: string]: string } = {
      'Passive': '🛡️',
      'Active': '⚔️',
      'Mastery': '👑',
      'Signature': '✨',
      'Utility': '🔧'
    };
    return iconMap[skill.skillCategory] || '❓';
  };

  const getSkillNodeClass = (skill: SkillNode): string => {
    if (skill.isLearned) return 'skill-node learned';
    if (skill.canLearn) return 'skill-node can-learn';
    if (skill.requiredLevel > (skillTreeData?.characterLevel || 1)) return 'skill-node locked';
    return 'skill-node cannot-learn';
  };

  const parseSkillEffects = (effectsJson: string): SkillEffectData | null => {
    try {
      return JSON.parse(effectsJson);
    } catch {
      return null;
    }
  };

  const getTierLabel = (tier: number): string => {
    const labels: { [key: number]: string } = {
      1: 'Tier I - Foundation',
      2: 'Tier II - Specialization',
      3: 'Tier III - Advanced',
      4: 'Tier IV - Master',
      5: 'Tier V - Legendary'
    };
    return labels[tier] || `Tier ${tier}`;
  };

  const handleSkillHover = (skillId: number, event: React.MouseEvent<HTMLDivElement>) => {
    setHoveredSkill(skillId);

    // Calculate tooltip position
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    setTooltipPosition({
      x: rect.left + scrollLeft + rect.width / 2,
      y: rect.top + scrollTop - 10 // Position above the skill node
    });
  };

  const handleSkillLeave = () => {
    setHoveredSkill(null);
    setTooltipPosition(null);
  };

  const handleSkillDragStart = (skill: SkillNode) => {
    // Only allow dragging active skills that are learned
    if (skill.isActive && skill.isLearned) {
      setDraggedSkill(skill);
    }
  };

  const handleHotbarDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleHotbarDrop = (e: React.DragEvent, hotbarSlot: number) => {
    e.preventDefault();
    if (draggedSkill) {
      equipSkillToHotbar(draggedSkill.id, hotbarSlot);
      setDraggedSkill(null);
    }
  };

  const renderSkillTooltip = () => {
    if (!hoveredSkill || !tooltipPosition || !skillTreeData) return null;

    const skill = skillTreeData.availableSkills.find(s => s.id === hoveredSkill);
    if (!skill) return null;

    const effects = parseSkillEffects(skill.skillEffects);
    const passiveAbility = skill.isLearned && effects?.PassiveAbilities?.[0]
      ? passiveDefinitions.find(def => def.name === effects.PassiveAbilities![0])
      : null;

    return (
      <div
        className="skill-tooltip"
        style={{
          position: 'absolute',
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          transform: 'translate(-50%, -100%)',
          zIndex: 1000
        }}
      >
        <div className="tooltip-header">
          <div className="tooltip-icon">{getSkillIcon(skill)}</div>
          <div className="tooltip-title">{skill.name}</div>
          <div className="tooltip-category">{skill.skillCategory}</div>
        </div>

        <div className="tooltip-description">{skill.description}</div>

        <div className="tooltip-requirements">
          <div className="tooltip-requirement-item">
            <span className="tooltip-label">Required Level:</span>
            <span className={skill.requiredLevel <= (skillTreeData.characterLevel || 1) ? 'tooltip-value-met' : 'tooltip-value-unmet'}>
              Lv {skill.requiredLevel}
            </span>
          </div>
          <div className="tooltip-requirement-item">
            <span className="tooltip-label">Cost:</span>
            <span className={skillTreeData.talentPoints.available >= skill.talentPointCost ? 'tooltip-value-met' : 'tooltip-value-unmet'}>
              {skill.talentPointCost} TP
            </span>
          </div>
        </div>

        {effects && (
          <div className="tooltip-effects">
            {effects.StatBonuses && Object.entries(effects.StatBonuses).some(([_, value]) => value > 0) && (
              <div className="tooltip-effects-section">
                <div className="tooltip-section-title">Stat Bonuses:</div>
                {Object.entries(effects.StatBonuses).map(([stat, value]) => (
                  value > 0 && (
                    <div key={stat} className="tooltip-effect-item">
                      <span>{stat}: +{value}</span>
                    </div>
                  )
                ))}
              </div>
            )}

            {effects.CombatBonuses && Object.entries(effects.CombatBonuses).some(([_, value]) => value > 0) && (
              <div className="tooltip-effects-section">
                <div className="tooltip-section-title">Combat Bonuses:</div>
                {Object.entries(effects.CombatBonuses).map(([bonus, value]) => (
                  value > 0 && (
                    <div key={bonus} className="tooltip-effect-item">
                      <span>{bonus}: +{value}%</span>
                    </div>
                  )
                ))}
              </div>
            )}

            {effects.PassiveAbilities && effects.PassiveAbilities.length > 0 && (
              <div className="tooltip-effects-section">
                <div className="tooltip-section-title">Passive Ability:</div>
                {effects.PassiveAbilities.map(ability => {
                  const abilityDef = passiveDefinitions.find(def => def.name === ability);
                  return (
                    <div key={ability} className="tooltip-passive-ability">
                      <span className="tooltip-passive-name">{ability}</span>
                      {abilityDef && (
                        <span className="tooltip-passive-desc">{abilityDef.description}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {skill.cannotLearnReason && (
          <div className="tooltip-warning">{skill.cannotLearnReason}</div>
        )}

        {skill.isLearned && (
          <div className="tooltip-status learned">✓ Learned</div>
        )}

        {skill.canLearn && !skill.isLearned && (
          <div className="tooltip-status can-learn">Click to learn</div>
        )}
      </div>
    );
  };

  const renderSkillConnections = () => {
    if (!skillTreeData || skillPositions.size === 0) return null;

    const connections: React.ReactElement[] = [];

    // Draw lines from prerequisites to dependents
    skillTreeData.availableSkills.forEach(skill => {
      if (!skill.prerequisiteSkillId) return;

      const fromPos = skillPositions.get(skill.prerequisiteSkillId);
      const toPos = skillPositions.get(skill.id);

      if (!fromPos || !toPos) return;

      // Determine line state
      const prerequisiteSkill = skillTreeData.availableSkills.find(s => s.id === skill.prerequisiteSkillId);
      const isActive = prerequisiteSkill?.isLearned && skill.isLearned;
      const isAvailable = prerequisiteSkill?.isLearned && skill.canLearn;
      const isHighlighted = hoveredSkill === skill.id || hoveredSkill === skill.prerequisiteSkillId;

      // Line styling based on state
      let strokeColor = '#4a5568'; // Default gray for locked
      let strokeWidth = 2;
      let strokeDasharray = '5,5'; // Dashed for locked

      if (isActive) {
        strokeColor = '#48bb78'; // Green for both learned
        strokeDasharray = 'none';
        strokeWidth = 3;
      } else if (isAvailable) {
        strokeColor = '#4299e1'; // Blue for available
        strokeDasharray = 'none';
        strokeWidth = 2;
      }

      if (isHighlighted) {
        strokeWidth += 1;
      }

      connections.push(
        <line
          key={`connection-${skill.prerequisiteSkillId}-${skill.id}`}
          x1={fromPos.x}
          y1={fromPos.y}
          x2={toPos.x}
          y2={toPos.y}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          opacity={isHighlighted ? 1 : 0.6}
          className="skill-connection"
        />
      );
    });

    return connections;
  };

  const getAvailableCategories = (): string[] => {
    if (!skillTreeData) return [];

    const categories = new Set<string>();
    skillTreeData.availableSkills.forEach(skill => {
      categories.add(skill.skillCategory);
    });

    return ['All', ...Array.from(categories).sort()];
  };

  const getCategoryIcon = (category: string): string => {
    const iconMap: { [key: string]: string } = {
      'All': '🌟',
      'Passive': '🛡️',
      'Active': '⚔️',
      'Mastery': '👑',
      'Signature': '✨',
      'Utility': '🔧'
    };
    return iconMap[category] || '❓';
  };

  const renderCategoryFilters = () => {
    const categories = getAvailableCategories();

    return (
      <div className="category-filters skill-category-filters">
        <div className="category-filters-label">Filter by Category:</div>
        <div className="category-filter-buttons">
          {categories.map(category => (
            <button
              key={category}
              className={`category-filter-button ${categoryFilter === category ? 'active' : ''}`}
              onClick={() => setCategoryFilter(category)}
            >
              <span className="category-filter-icon">{getCategoryIcon(category)}</span>
              <span className="category-filter-text">{category}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderSkillHotbar = () => {
    const hotbarSlots = [0, 1, 2, 3, 4, 5, 6, 7];

    return (
      <div className="skill-hotbar-container">
        <div className="hotbar-header">
          <div className="hotbar-title">⚔️ Active Skills Hotbar</div>
          <div className="hotbar-hint">Drag active skills here to use in battle</div>
        </div>
        <div className="skill-hotbar">
          {hotbarSlots.map(slot => {
            const equippedSkill = equippedSkills.find(es => es.hotbarSlot === slot);

            return (
              <div
                key={slot}
                className={`hotbar-slot ${equippedSkill ? 'filled' : 'empty'}`}
                onDragOver={handleHotbarDragOver}
                onDrop={(e) => handleHotbarDrop(e, slot)}
                onClick={() => equippedSkill && unequipSkillFromHotbar(slot)}
                title={equippedSkill ? `Click to remove ${equippedSkill.skill.name}` : `Slot ${slot + 1} - Empty`}
              >
                <div className="hotbar-slot-number">{slot + 1}</div>
                {equippedSkill ? (
                  <div className="hotbar-skill">
                    <div className="hotbar-skill-icon">{getSkillIcon(equippedSkill.skill)}</div>
                    <div className="hotbar-skill-name">{equippedSkill.skill.name}</div>
                    {equippedSkill.skill.apCost && (
                      <div className="hotbar-skill-cost">AP: {equippedSkill.skill.apCost}</div>
                    )}
                    {equippedSkill.skill.cooldown && (
                      <div className="hotbar-skill-cooldown">CD: {equippedSkill.skill.cooldown}</div>
                    )}
                  </div>
                ) : (
                  <div className="hotbar-empty-icon">+</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSkillGrid = () => {
    if (!skillTreeData) return null;

    // Filter skills by category
    const filteredSkills = categoryFilter === 'All'
      ? skillTreeData.availableSkills
      : skillTreeData.availableSkills.filter(skill => skill.skillCategory === categoryFilter);

    // Group skills by tier for grid layout
    const skillsByTier: { [tier: number]: SkillNode[] } = {};
    filteredSkills.forEach(skill => {
      if (!skillsByTier[skill.treeTier]) {
        skillsByTier[skill.treeTier] = [];
      }
      skillsByTier[skill.treeTier].push(skill);
    });

    // Sort skills within each tier by position
    Object.keys(skillsByTier).forEach(tier => {
      skillsByTier[parseInt(tier)].sort((a, b) => a.treePosition - b.treePosition);
    });

    return (
      <div className="skill-tree-tiers">
        {/* SVG overlay for connection lines */}
        <svg className="skill-connections-svg" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0
        }}>
          {renderSkillConnections()}
        </svg>

        {Object.entries(skillsByTier)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .map(([tier, skills]) => (
            <div key={tier} className="skill-tier-container">
              <div className="tier-label">{getTierLabel(parseInt(tier))}</div>
              <div className="skill-tier-row">
                {skills.map(skill => (
                  <div
                    key={skill.id}
                    ref={(el) => {
                      if (el) {
                        skillRefs.current.set(skill.id, el);
                      }
                    }}
                    className={`${getSkillNodeClass(skill)} ${selectedSkill?.id === skill.id ? 'selected' : ''} ${skill.isActive && skill.isLearned ? 'draggable' : ''}`}
                    draggable={skill.isActive && skill.isLearned}
                    onClick={() => {
                      // If skill can be learned, learn it directly
                      if (skill.canLearn && !skill.isLearned) {
                        learnSkill(skill.id);
                      } else {
                        // Otherwise just select it to show details
                        setSelectedSkill(skill);
                      }
                    }}
                    onMouseEnter={(e) => handleSkillHover(skill.id, e)}
                    onMouseLeave={handleSkillLeave}
                    onDragStart={() => handleSkillDragStart(skill)}
                    onDragEnd={() => setDraggedSkill(null)}
                  >
                    <div className="skill-icon">
                      {getSkillIcon(skill)}
                    </div>
                    <div className="skill-name">{skill.name}</div>
                    <div className="skill-category-badge">{skill.skillCategory}</div>
                    <div className="skill-cost">{skill.talentPointCost} TP</div>
                    <div className="skill-requirement">Lv {skill.requiredLevel}</div>
                    {skill.isLearned && <div className="learned-checkmark">✓</div>}
                    {skill.isLearned && (() => {
                      const learnedSkill = skillTreeData.learnedSkills.find(ls => ls.skillNodeId === skill.id);
                      if (learnedSkill && learnedSkill.skillLevel > 0) {
                        return (
                          <div className="skill-level-badge" title={`${learnedSkill.skillXP} / ${learnedSkill.xpToNextLevel} XP`}>
                            Lv {learnedSkill.skillLevel}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
    );
  };

  const renderSkillDetails = () => {
    if (!selectedSkill) return null;

    const effects = parseSkillEffects(selectedSkill.skillEffects);
    const passiveAbility = selectedSkill.isLearned && effects?.PassiveAbilities?.[0]
      ? passiveDefinitions.find(def => def.name === effects.PassiveAbilities![0])
      : null;

    const learnedSkill = selectedSkill.isLearned
      ? skillTreeData?.learnedSkills.find(ls => ls.skillNodeId === selectedSkill.id)
      : null;

    return (
      <div className="skill-details-panel">
        <div className="skill-details-title">{selectedSkill.name}</div>
        <div className="skill-details-description">{selectedSkill.description}</div>

        <div className="skill-info">
          <div className="skill-effect-item">
            <span className="skill-effect-label">Category:</span>
            <span className="skill-effect-value">{selectedSkill.skillCategory}</span>
          </div>
          <div className="skill-effect-item">
            <span className="skill-effect-label">Required Level:</span>
            <span className="skill-effect-value">{selectedSkill.requiredLevel}</span>
          </div>
          <div className="skill-effect-item">
            <span className="skill-effect-label">Talent Cost:</span>
            <span className="skill-effect-value">{selectedSkill.talentPointCost}</span>
          </div>
          {learnedSkill && learnedSkill.skillLevel > 0 && (
            <>
              <div className="skill-effect-item">
                <span className="skill-effect-label">Skill Level:</span>
                <span className="skill-effect-value skill-level-highlight">Level {learnedSkill.skillLevel}</span>
              </div>
              <div className="skill-effect-item">
                <span className="skill-effect-label">Skill XP:</span>
                <span className="skill-effect-value">{learnedSkill.skillXP} / {learnedSkill.xpToNextLevel}</span>
              </div>
              <div className="skill-xp-bar">
                <div
                  className="skill-xp-progress"
                  style={{ width: `${(learnedSkill.skillXP / learnedSkill.xpToNextLevel) * 100}%` }}
                ></div>
              </div>
              <div className="skill-effect-item">
                <span className="skill-effect-label">Times Used:</span>
                <span className="skill-effect-value">{learnedSkill.timesUsed}</span>
              </div>
            </>
          )}
        </div>

        {effects && (
          <div className="skill-effects">
            <div className="skill-effects-title">Effects:</div>
            
            {effects.StatBonuses && Object.entries(effects.StatBonuses).map(([stat, value]) => (
              value > 0 && (
                <div key={stat} className="skill-effect-item">
                  <span className="skill-effect-label">{stat}:</span>
                  <span className="skill-effect-value">+{value}</span>
                </div>
              )
            ))}
            
            {effects.CombatBonuses && Object.entries(effects.CombatBonuses).map(([bonus, value]) => (
              value > 0 && (
                <div key={bonus} className="skill-effect-item">
                  <span className="skill-effect-label">{bonus}:</span>
                  <span className="skill-effect-value">+{value}%</span>
                </div>
              )
            ))}

            {effects.PassiveAbilities && effects.PassiveAbilities.map(ability => (
              <div key={ability} className="skill-effect-item">
                <span className="skill-effect-label">Passive:</span>
                <span className="skill-effect-value">{ability}</span>
              </div>
            ))}
          </div>
        )}

        {passiveAbility && (
          <div className="passive-ability-details">
            <div className="skill-effects-title">Passive Ability:</div>
            <div className="skill-details-description">{passiveAbility.description}</div>
          </div>
        )}

        {selectedSkill.canLearn && !selectedSkill.isLearned && (
          <button
            className="learn-skill-button"
            onClick={() => learnSkill(selectedSkill.id)}
            disabled={learningSkill === selectedSkill.id}
          >
            {learningSkill === selectedSkill.id ? 'Learning...' : `Learn Skill (${selectedSkill.talentPointCost} points)`}
          </button>
        )}

        {selectedSkill.cannotLearnReason && (
          <div className="error-message">{selectedSkill.cannotLearnReason}</div>
        )}
      </div>
    );
  };

  const renderPassiveBonuses = () => {
    if (!skillTreeData) return null;

    const { passiveBonuses } = skillTreeData;
    const hasStatBonuses = Object.values(passiveBonuses.statBonuses).some(value => value > 0);
    const hasCombatBonuses = Object.values(passiveBonuses.combatBonuses).some(value => value > 0);

    return (
      <div className="passive-bonuses-section">
        <div className="passive-bonuses-title">Passive Bonuses</div>
        
        {hasStatBonuses && (
          <>
            <div className="skill-effects-title">Stat Bonuses:</div>
            <div className="stat-bonuses-grid">
              {Object.entries(passiveBonuses.statBonuses).map(([stat, value]) => (
                value > 0 && (
                  <div key={stat} className="stat-bonus-item">
                    <span className="stat-bonus-label">{stat}:</span>
                    <span className="stat-bonus-value">+{value}</span>
                  </div>
                )
              ))}
            </div>
          </>
        )}

        {hasCombatBonuses && (
          <>
            <div className="skill-effects-title">Combat Bonuses:</div>
            <div className="stat-bonuses-grid">
              {Object.entries(passiveBonuses.combatBonuses).map(([bonus, value]) => (
                value > 0 && (
                  <div key={bonus} className="stat-bonus-item">
                    <span className="stat-bonus-label">{bonus}:</span>
                    <span className="stat-bonus-value">+{value}%</span>
                  </div>
                )
              ))}
            </div>
          </>
        )}

        {passiveBonuses.passiveAbilities.length > 0 && (
          <div className="passive-abilities-list">
            <div className="passive-abilities-title">Passive Abilities:</div>
            {passiveBonuses.passiveAbilities.map(ability => {
              const definition = passiveDefinitions.find(def => def.name === ability);
              return (
                <div key={ability} className="passive-ability-item" title={definition?.description}>
                  {ability}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderTalentHistory = () => {
    if (talentHistory.length === 0) return null;

    return (
      <div className="talent-history-section">
        <div className="talent-history-title">Talent Point History</div>
        {talentHistory.slice(0, 10).map(transaction => (
          <div 
            key={transaction.id} 
            className={`talent-transaction ${transaction.pointChange > 0 ? 'positive' : 'negative'}`}
          >
            <div>
              <div className={`talent-transaction-points ${transaction.pointChange > 0 ? 'positive' : 'negative'}`}>
                {transaction.pointChange > 0 ? '+' : ''}{transaction.pointChange}
              </div>
              <div className="talent-transaction-source">{transaction.source}</div>
            </div>
            <div>{transaction.relatedSkillName || transaction.description}</div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="loading-spinner">Loading skill tree...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!skillTreeData) {
    return <div className="error-message">No skill tree data available</div>;
  }

  // Check if skill tree is empty
  if (skillTreeData.availableSkills.length === 0) {
    return (
      <div className="skill-tree-empty-state">
        <div className="empty-state-icon">🌳</div>
        <h2>Skill Tree Not Yet Available</h2>
        <p>The skill tree system is ready, but no skills have been configured yet.</p>
        <p className="empty-state-info">
          Skills need to be seeded into the database first. Contact an administrator or use the admin panel to seed skills.
        </p>
        <div className="empty-state-hint">
          <strong>For Administrators:</strong>
          <p>Use the endpoint: POST /api/skilltree/admin/seed-skills</p>
        </div>
      </div>
    );
  }

  return (
    <div className="skill-tree-container">
      {renderSkillTooltip()}

      {/* Success Message */}
      {successMessage && (
        <div className="success-notification">
          {successMessage}
        </div>
      )}

      <div className="skill-tree-main">
        <div className="skill-tree-header">
          <div className="skill-tree-title">
            {skillTreeData.characterClass} Skill Tree
          </div>
          <div className="talent-points-display">
            {skillTreeData.talentPoints.available} Talent Points
          </div>
        </div>

        {skillTreeData.learnedSkills.length > 0 && (
          <div className="reset-skills-section">
            <button
              className="reset-skills-button"
              onClick={() => setShowResetConfirm(true)}
              disabled={resetting}
            >
              {resetting ? 'Resetting...' : 'Reset All Skills'}
            </button>
          </div>
        )}

        {renderCategoryFilters()}
        {renderSkillHotbar()}
        {renderSkillGrid()}
        {renderSkillDetails()}
      </div>

      <div className="skill-tree-sidebar">
        {renderPassiveBonuses()}
        {renderTalentHistory()}
      </div>

      {showResetConfirm && (
        <div className="modal-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">Reset All Skills?</div>
            <div className="modal-body">
              <p>Are you sure you want to reset all learned skills?</p>
              <p>You will refund <strong>{skillTreeData.talentPoints.totalSpent} talent points</strong> and lose all {skillTreeData.learnedSkills.length} learned skills.</p>
              <p className="modal-warning">This action cannot be undone!</p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-button modal-button-cancel"
                onClick={() => setShowResetConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="modal-button modal-button-confirm"
                onClick={resetSkills}
              >
                Reset Skills
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillTree;